// src/context/site/urlDelta.js
//
// Pure helpers behind the Site Manager's "edit locally, save once" model.
//
// The modal never PATCHes a row on click. Every checkbox change is a PENDING
// desired state keyed by URL, and "Salva modifiche" folds all of them into ONE
// delta sent under ONE If-Match. Pending state is keyed by URL rather than by
// row index so it survives paging and searching; `baseline` holds every server
// row the user has seen under the current etag, so a desired state can always
// be compared against what the server holds.
//
// ⛔ Two contract facts live here and nowhere else:
//   • `add` does NOT pin — a pinned-by-default add goes in BOTH `add` and `pin`
//     of the same delta (one request, one ETag round-trip);
//   • a row excluded by a `…/*` PATTERN is read-only on this surface — a delta
//     naming it is a 422, so computeDelta never emits one.

/** The six imperative operations, in the order the API documents them. */
export const DELTA_KEYS = ['pin', 'unpin', 'exclude', 'unexclude', 'add', 'remove'];

/** The six PAST-TENSE keys of the PATCH response's `applied` (always all present on the wire). */
export const APPLIED_KEYS = ['pinned', 'unpinned', 'added', 'removed', 'excluded', 'unexcluded'];

export function emptyDelta() {
  return { pin: [], unpin: [], exclude: [], unexclude: [], add: [], remove: [] };
}

/**
 * @param {object} args
 * @param {Map<string, object>} args.baseline url → server row (every row seen under the current etag)
 * @param {Map<string, {pinned?: boolean, excluded?: boolean}>} args.pending url → desired flags (touched rows only)
 * @param {Array<{url: string, category?: string, pin: boolean}>} [args.added]
 * @param {Set<string>} [args.removed]
 * @returns {{pin: string[], unpin: string[], exclude: string[], unexclude: string[], add: object[], remove: string[]}}
 */
export function computeDelta({ baseline, pending, added = [], removed = new Set() }) {
  const delta = emptyDelta();
  pending.forEach((desired, url) => {
    const row = baseline.get(url);
    if (!row) return;            // a row we no longer hold — nothing to compare against
    if (removed.has(url)) return; // a removed row's flags are moot
    if (typeof desired.pinned === 'boolean' && desired.pinned !== row.pinned) {
      (desired.pinned ? delta.pin : delta.unpin).push(url);
    }
    if (
      typeof desired.excluded === 'boolean'
      && desired.excluded !== row.excluded
      && row.excluded_by !== 'pattern'
    ) {
      (desired.excluded ? delta.exclude : delta.unexclude).push(url);
    }
  });
  added.forEach(({ url, category, pin }) => {
    delta.add.push(category ? { url, category } : { url });
    if (pin) delta.pin.push(url); // ⚠️ `add` does NOT pin — both ride the same request
  });
  removed.forEach((url) => delta.remove.push(url));
  return delta;
}

export function isEmptyDelta(delta) {
  return DELTA_KEYS.every((k) => !Array.isArray(delta?.[k]) || delta[k].length === 0);
}

/**
 * How many PAGES the user has changed (drives the footer + the Save gate). v201: a page counts once even when its
 * change rides two keys (excluding a still-pinned page also unpins it — the user made ONE change).
 */
export function countPending({ baseline, pending, added = [], removed = new Set() }) {
  const d = computeDelta({ baseline, pending, added: [], removed });
  const touched = new Set([...d.pin, ...d.unpin, ...d.exclude, ...d.unexclude, ...removed]);
  return touched.size + added.length;
}

/** A server row overlaid with its pending desired flags — what the checkbox shows. */
export function effectiveRow(row, pending) {
  const desired = pending.get(row.url);
  if (!desired) return row;
  return {
    ...row,
    pinned: typeof desired.pinned === 'boolean' ? desired.pinned : row.pinned,
    excluded: typeof desired.excluded === 'boolean' ? desired.excluded : row.excluded,
  };
}

/**
 * The next pending map after the user set one flag on one row. An entry that
 * lands back on the server's own state is DROPPED, so "touched then untouched"
 * counts as no change.
 */
export function withFlag(pending, row, key, value) {
  const next = new Map(pending);
  const merged = { ...(next.get(row.url) || {}), [key]: value };
  const samePinned = (typeof merged.pinned === 'boolean' ? merged.pinned : row.pinned) === row.pinned;
  const sameExcluded = (typeof merged.excluded === 'boolean' ? merged.excluded : row.excluded) === row.excluded;
  if (samePinned && sameExcluded) next.delete(row.url);
  else next.set(row.url, merged);
  return next;
}

/** Read the response counts by their PAST-TENSE keys; a missing key is 0, never undefined. */
export function readApplied(applied) {
  const out = {};
  APPLIED_KEYS.forEach((k) => { out[k] = Number(applied?.[k]) || 0; });
  return out;
}

/** Normalise a typed URL to its absolute http(s) form; null when it is not one. */
export function normaliseUrl(input) {
  const s = (input || '').trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.href;
  } catch {
    return null;
  }
}
